/** MEWS product IDs for variable-count upsells (1–4). Base = 1 person (absolute); count2/3/4 = relative products. */
export interface MewsVariableCountIds {
  base: string;
  count2: string;
  count3: string;
  count4: string;
}

/** Local rate plan for which MEWS ProductIds to use on reservations / browse. */
export type MewsUpsellRatePlan = "standard" | "signature";

export interface Upsell {
  id: string;
  /** MEWS ProductId for availability/reservation (single product); omit if using mewsVariableCount or not in MEWS */
  mewsProductId?: string;
  /** Signature-rate duplicate of `mewsProductId` when priced separately in MEWS. */
  mewsProductIdSignature?: string;
  /**
   * Optional separate MEWS product for child pricing when this add-on is PerPerson in Mews.
   * If omitted, browse/summary uses the adult unit for all guests.
   */
  mewsChildProductId?: string;
  /** For massage packages 20/30/40 min: base + relative product IDs (count 1 = base only; 2–4 = base + relative). Omit for single-product upsells. */
  mewsVariableCount?: MewsVariableCountIds;
  /** Signature-rate duplicate set; same shape as `mewsVariableCount`. Relative lines must pair with this base on signature reservations. */
  mewsVariableCountSignature?: MewsVariableCountIds;
  name: string;
  description: string;
  /** Selling price (CHF) — from MEWS when wired, else listPrice * 0.9. For variable-count this is the 1-person price. */
  price: number;
  /** List price (CHF) — shown slashed in UI when > price (10% online discount) */
  listPrice: number;
  image: string;
  category: "Dining" | "Romance" | "Spa & Massage" | "Sauna & Thermal";
  /** Shown when "Popular" filter is selected. */
  popular?: boolean;
}

/** Whether this upsell uses a +/- count (1–4) instead of a simple add/remove. */
export function isVariableCountUpsell(upsell: Upsell): boolean {
  return !!upsell.mewsVariableCount;
}

/**
 * Variable-count definition for pricing/reservation for the given rate plan.
 * Signature uses `mewsVariableCountSignature` when set; otherwise falls back to standard `mewsVariableCount`.
 */
export function effectiveMewsVariableCountIds(
  upsell: Upsell,
  ratePlan: MewsUpsellRatePlan,
): MewsVariableCountIds | undefined {
  if (ratePlan === "signature" && upsell.mewsVariableCountSignature) {
    return upsell.mewsVariableCountSignature;
  }
  return upsell.mewsVariableCount;
}

/** Base or single ProductId used for browse/card pricing from `hotels/Products` for this rate plan. */
export function upsellPrimaryMewsProductIdForBrowse(
  upsell: Upsell,
  ratePlan: MewsUpsellRatePlan,
): string | undefined {
  const v = effectiveMewsVariableCountIds(upsell, ratePlan);
  if (v) return v.base;
  if (ratePlan === "signature" && upsell.mewsProductIdSignature) {
    return upsell.mewsProductIdSignature;
  }
  return upsell.mewsProductId;
}

/**
 * All signature-rate ProductIds → local upsell id (for `mapService` merge; keeps env signature JSON optional).
 */
export function collectSignatureProductIdsForUpsellMapping(upsells: readonly Upsell[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const u of upsells) {
    if (u.mewsProductIdSignature) out[u.mewsProductIdSignature] = u.id;
    const v = u.mewsVariableCountSignature;
    if (v) {
      out[v.base] = u.id;
      out[v.count2] = u.id;
      out[v.count3] = u.id;
      out[v.count4] = u.id;
    }
  }
  return out;
}

/**
 * Returns MEWS ProductIds to send for this upsell and guest count for the given rate plan.
 */
export function getMewsProductIdsForUpsellForRatePlan(
  upsell: Upsell,
  count: number,
  ratePlan: MewsUpsellRatePlan,
): string[] {
  if (count <= 0) return [];
  const v = effectiveMewsVariableCountIds(upsell, ratePlan);
  if (v) {
    if (count === 1) return [v.base];
    if (count === 2) return [v.base, v.count2];
    if (count === 3) return [v.base, v.count3];
    if (count === 4) return [v.base, v.count4];
    return [v.base];
  }
  const single =
    ratePlan === "signature"
      ? (upsell.mewsProductIdSignature ?? upsell.mewsProductId)
      : upsell.mewsProductId;
  if (single) return [single];
  return [];
}

/**
 * Returns MEWS ProductIds to send for this upsell and count (**standard** rate — backward compatible).
 * Variable-count: count 1 → [base]; 2 → [base, count2]; 3 → [base, count3]; 4 → [base, count4]; 0 → [].
 * Single-product: count ≥ 1 → [mewsProductId]; 0 → [].
 */
export function getMewsProductIdsForUpsell(upsell: Upsell, count: number): string[] {
  return getMewsProductIdsForUpsellForRatePlan(upsell, count, "standard");
}

/** 10% below list price (prices in MEWS); UI shows listPrice slashed. */
const sell = (list: number) => Math.round(list * 0.9);

/**
 * Resolves price and listPrice for an upsell from MEWS product price map when available.
 * Selling price from MEWS; list price = selling price / 0.9 (so list is 10% higher for display).
 * Falls back to upsell.price and upsell.listPrice when product not in map.
 */
export function getResolvedUpsellPrices(
  upsell: Upsell,
  productPriceMap: Map<string, number>
): { price: number; listPrice: number } {
  const productId = upsell.mewsVariableCount?.base ?? upsell.mewsProductId;
  const mewsPrice = productId != null ? productPriceMap.get(productId) : undefined;
  if (mewsPrice != null && mewsPrice > 0) {
    return {
      price: mewsPrice,
      listPrice: Math.round(mewsPrice / 0.9),
    };
  }
  return { price: upsell.price, listPrice: upsell.listPrice };
}

export const UPSELLS: Upsell[] = [
  {
    id: "massage-express-25",
    mewsVariableCount: {
      base: "46e69aea-8e6e-4b54-b058-b3e700d23f95",
      count2: "b27f256c-ba2c-427d-b9b3-b42200c39ac4",
      count3: "03008bc0-fa36-494e-960e-b42200c3d7af",
      count4: "1883f4be-454e-42e8-9e7c-b42200c48259",
    },
    mewsVariableCountSignature: {
      base: "b77129ee-968f-473c-ac24-b42200c2ec84",
      count2: "08e536f6-0a5e-474f-ac01-b3e70115405f",
      count3: "3e3cf7a1-29be-492d-94ca-b3e7011641b4",
      count4: "01696d13-0c0f-46ff-b1ff-b3e70116a2b7",
    },
    name: "Alpine Wellness Taster (20 min)",
    description:
      "Short NutraSwiss spa session when time is tight. On arrival, tell us your focus and we'll match the treatment.",
    listPrice: 44,
    price: sell(44),
    image: "/images/Clinique Nutraswiss/massage images/Sport Massage.jpg",
    category: "Spa & Massage",
  },
  {
    id: "massage-relaxation-50",
    mewsVariableCount: {
      base: "f1424e35-4b1e-4c8c-8a05-b3e700cd78c2",
      count2: "84ea72df-81ac-4cbd-aba3-b3e701174589",
      count3: "89fd8f48-7f46-4f13-84a7-b3e70117838d",
      count4: "66ce9ef8-973f-49ec-990c-b3e701189d90",
    },
    mewsVariableCountSignature: {
      base: "c0da79d4-284f-4805-9204-b42200c8a06a",
      count2: "ce3c3305-4414-446f-a247-b42200c8f496",
      count3: "457b50a2-9109-4b20-8145-b42200c95bc7",
      count4: "e5fbf02e-c55d-417e-8758-b42200c9a002",
    },
    name: "Relaxation Package (30 min)",
    description:
      "One 30-minute NutraSwiss treatment. Our team helps you choose on arrival—unwind, ease muscles, or focus on back and feet.",
    listPrice: 72,
    price: sell(72),
    image: "/images/Clinique Nutraswiss/massage images/back and neck pain.jpg",
    category: "Spa & Massage",
  },
  {
    id: "massage-pamper-75",
    mewsVariableCount: {
      base: "01ca1a94-296d-428a-bb0e-b3e700ced0b0",
      count2: "25997e34-81f0-4c3f-afdb-b3e701196112",
      count3: "3818898f-e9f5-4016-b70c-b3e701199d45",
      count4: "45325bcc-cbb1-4b1f-8582-b3e70119cfdd",
    },
    mewsVariableCountSignature: {
      base: "326822d6-f308-44ec-83f2-b42200c54df1",
      count2: "794df6bf-af8a-4858-bb51-b42200c5b0b6",
      count3: "b3f79778-fb5c-497e-9cdb-b42200c5e479",
      count4: "edd76228-14b1-4c1e-b1a1-b42200c66a9b",
    },
    name: "Pamper Package (40 min)",
    description:
      "40 minutes at our NutraSwiss spa. At check-in we help you pick deep relaxation, recovery, or a nourishing body treatment.",
    listPrice: 131,
    price: sell(131),
    image: "/images/Clinique Nutraswiss/massage images/Full body.jpg",
    category: "Spa & Massage",
  },
  {
    id: "sauna-full",
    mewsVariableCount: {
      base: "930ff630-d6ed-403e-ae6b-b3e7017060cc",
      count2: "55d595f8-b332-4f82-8a87-b3e7016d9e49",
      count3: "ba2e9824-11fa-4e44-ac65-b3e7016dd9c6",
      count4: "c4c6c337-8f50-43d8-a371-b3e7016e16fc",
    },
    name: "Sauna & Spa Day Package",
    description: "Full thermal area + complimentary drinks. Per person.",
    listPrice: 90,
    price: sell(90),
    image: "/images/Clinique Nutraswiss/Sauna package images/Finnish Sauna.jpg",
    category: "Sauna & Thermal",
    popular: true,
  },
  {
    id: "sauna-heat",
    mewsVariableCount: {
      base: "81cd4af6-18bd-4ae5-8a49-b3e700d72778",
      count2: "35a09bef-4b61-4bee-a1fe-b3e800e429c8",
      count3: "9169a4c7-1478-44b1-8309-b3e800e47c20",
      count4: "eb2f02ba-11b4-426a-8e64-b3e800e7e20b",
    },
    name: "Heat Experience (Finnish & Bio Sauna)",
    description: "Day access to Finnish and Bio sauna.",
    listPrice: 50,
    price: sell(50),
    image: "/images/Clinique Nutraswiss/Sauna package images/Bio Sauna.jpg",
    category: "Sauna & Thermal",
  },
  {
    id: "sauna-steam-ice",
    mewsVariableCount: {
      base: "80a26693-bf44-469c-8990-b3e700d9ffdd",
      count2: "cb057ebc-32d5-4239-8563-b3e70175508a",
      count3: "973ef144-5a67-4ea2-8527-b3e701759f67",
      count4: "a8157f6d-0811-46be-82ed-b3e70175e6e5",
    },
    name: "Steam & Ice Experience",
    description: "Day access to steam bath and ice plunge.",
    listPrice: 50,
    price: sell(50),
    image: "/images/Clinique Nutraswiss/Sauna package images/Steam Bath.jpg",
    category: "Sauna & Thermal",
  },
  {
    id: "dinner-5-course",
    mewsProductId: "62971d74-62d5-4ad3-ac63-b4210068d340",
    mewsProductIdSignature: "748c2c58-df62-4570-a2a3-b42200c090b0",
    name: "5-Course Dinner at Restaurant Jungfrau",
    description: "5-course menu; select main on arrival. Per person per night.",
    listPrice: 55,
    price: sell(55),
    image: "/images/booking-page/5c%20dinner.webp",
    category: "Dining",
  },
  {
    id: "romantic-setup",
    mewsProductId: "b602e294-7ab9-47f4-937e-b3e700e0cfa0",
    mewsProductIdSignature: "319872d8-71c7-4977-a62c-b42200bdd1f5",
    name: "Romantic Decoration",
    description: "Chocolate and Prosecco in room on arrival.",
    listPrice: 45,
    price: sell(45),
    image: "/images/booking-page/prosecco.webp",
    category: "Romance",
  },
];
